import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';

const CustomerAgreement = () => {
  return (
    <LegalPageLayout
      title="Customer Agreement"
      updated="October 13, 2026"
    >
      <Typography paragraph>
        This Customer Agreement applies to individuals and
        organizations using HaggleFreight to post shipments,
        communicate with Transportation Providers, negotiate,
        confirm transportation, or use related marketplace
        features.
      </Typography>

      <Typography paragraph>
        This agreement is with GoCourier Inc., operator of
        HaggleFreight. It supplements the Terms of Service,
        Acceptable Use Policy, Refund Policy, and Cancellation
        Policy when presented and accepted. The Business Customer
        Agreement also applies when accepted for an organizational
        account.
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Customer Responsibilities
      </Typography>

      <Typography paragraph>
        You must be at least 18 years old, legally able to
        contract, and authorized to offer the shipment. When
        representing a business or cargo owner, accurately identify
        your role and confirm that you have the relevant authority.
      </Typography>

      <Typography paragraph>
        Provide accurate account and shipment information, protect
        account access, review commitments before accepting them,
        and promptly communicate material changes. Do not
        impersonate another business or offer cargo you lack
        authority to ship.
      </Typography>

      <Typography paragraph>
        A Customer account does not grant a license, operating
        authority, or permission to perform a regulated activity.
        Any intermediary activity remains subject to its own
        applicable legal requirements.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Shipment Information
      </Typography>

      <Typography paragraph>
        Describe cargo accurately, including contents, quantity,
        dimensions, weight, value where requested, timing,
        pickup and delivery locations, and special requirements.
      </Typography>

      <Typography paragraph>
        Disclose refrigeration, oversized cargo, liftgate or
        forklift needs, loading arrangements, appointments,
        facility restrictions, and other information materially
        affecting price or performance before acceptance.
      </Typography>

      <Typography paragraph>
        Correct errors promptly and obtain agreement to material
        changes. Do not place unnecessary sensitive information
        in broadly visible listings.
      </Typography>

      <Typography variant="h5" gutterBottom>
        3. Prohibited and Restricted Items
      </Typography>

      <Typography paragraph>
        Comply with applicable law and the Acceptable Use Policy.
        Illegal, stolen, counterfeit, or otherwise unlawful cargo
        is prohibited. The Platform may also exclude categories
        that could be lawful under different circumstances.
      </Typography>

      <Typography paragraph>
        Disclosure alone, the ability to enter an item in a
        listing, or a provider's willingness to carry it does not
        override a Platform restriction or legal requirement.
        Do not conceal regulated characteristics.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. Packaging and Loading
      </Typography>

      <Typography paragraph>
        Prepare, package, label, and document cargo appropriately,
        unless the performing Transportation Provider expressly
        agrees to undertake a particular task and may lawfully
        do so.
      </Typography>

      <Typography paragraph>
        Identify loading and unloading responsibilities, equipment
        needs, dock access, extra labor, and facility conditions
        before confirmation. Do not assume an additional service
        is included when the agreement does not provide for it.
      </Typography>

      <Typography paragraph>
        Platform records do not replace a bill of lading,
        shipping document, permit, or other legally required
        record. Each party remains responsible for the
        documentation duties that apply to it.
      </Typography>

      <Typography variant="h5" gutterBottom>
        5. Pricing and Payments
      </Typography>

      <Typography paragraph>
        You are responsible for transportation charges you agree
        to and other amounts supported by accepted terms or
        applicable law. Review the amount, recipient, payment
        conditions, and relevant cancellation terms before
        authorizing a transaction.
      </Typography>

      <Typography paragraph>
        Waiting time, cancellation, return transportation,
        redelivery, storage, and other additional charges require
        an applicable contractual or legal basis. A general
        reference to possible charges does not authorize an
        undisclosed amount.
      </Typography>

      <Typography paragraph>
        HaggleFreight's own charges are separate from transportation
        charges. This agreement does not establish a future
        subscription price or fixed free-access duration.
        Future paid offers require the disclosures and
        authorization described in the Terms of Service.
      </Typography>

      <Typography paragraph>
        Accepting this agreement or storing a payment method does
        not authorize unknown future fees, penalties, adjustments,
        or recurring billing. Good-faith payment-dispute rights
        remain available.
      </Typography>

      <Typography variant="h5" gutterBottom>
        6. Bids and Negotiation
      </Typography>

      <Typography paragraph>
        Review the proposed provider, cargo requirements, price,
        and service terms. A bid or discussion is not automatically
        a confirmed transportation agreement. The parties must
        mutually accept the material terms and satisfy clearly
        disclosed booking conditions.
      </Typography>

      <Typography paragraph>
        Automated suggestions and profile indicators do not
        guarantee pricing, availability, compliance, or
        performance. You remain responsible for the choices
        and commitments you make.
      </Typography>

      <Typography paragraph>
        Material changes require the affected parties' agreement.
        Neither party may use a later message or status update
        to impose an undisclosed charge or different service
        without an applicable basis.
      </Typography>

      <Typography variant="h5" gutterBottom>
        7. Independent Transportation Providers
      </Typography>

      <Typography paragraph>
        Physical transportation is performed by the relevant
        Transportation Provider, not by GoCourier Inc. Customers
        and providers make their own transportation agreements.
      </Typography>

      <Typography paragraph>
        Providers are responsible for the qualifications,
        vehicles, personnel, insurance, and operational
        requirements applicable to their services. Account
        approval or a document upload does not guarantee
        suitability for every shipment.
      </Typography>

      <Typography paragraph>
        These descriptions do not waive a duty imposed on
        GoCourier Inc. or determine a regulatory classification
        contrary to actual operations and applicable law.
      </Typography>

      <Typography variant="h5" gutterBottom>
        8. Pickup and Delivery Access
      </Typography>

      <Typography paragraph>
        Provide safe and lawful access to the extent within your
        control. Communicate relevant contacts, gate instructions,
        loading arrangements, parking restrictions, appointments,
        and recipient requirements.
      </Typography>

      <Typography paragraph>
        Arrange for cargo availability and receipt as agreed.
        Notify the provider promptly of changes or access
        problems. Do not require a driver to violate traffic,
        safety, cargo, or hours-of-service obligations.
      </Typography>

      <Typography variant="h5" gutterBottom>
        9. Shipment Issues and Claims
      </Typography>

      <Typography paragraph>
        Report missing, delayed, damaged, or disputed shipments
        promptly to the Transportation Provider and relevant
        insurer. Contact HaggleFreight for available Platform
        support and preserve relevant records.
      </Typography>

      <Typography paragraph>
        Provide truthful information and reasonably necessary
        evidence. Support review does not extend a legal,
        contractual, insurance, or payment-provider deadline.
        Do not seek duplicate compensation for the same loss.
      </Typography>

      <Typography paragraph>
        The Refund Policy and Cancellation Policy govern their
        respective subjects. They do not eliminate nonwaivable
        rights or replace a formal cargo-claim process.
      </Typography>

      <Typography variant="h5" gutterBottom>
        10. Delivery Records and Personal Information
      </Typography>

      <Typography paragraph>
        Available delivery-record features may include photographs,
        signatures, timestamps, location information, notes,
        or documents. Such records are evidence, not an automatic
        waiver of a legitimate claim.
      </Typography>

      <Typography paragraph>
        Submit truthful confirmations and only information you
        have authority to provide. Use information about drivers,
        recipients, facilities, and other users only for the
        relevant transaction, lawful recordkeeping, or another
        authorized purpose.
      </Typography>

      <Typography paragraph>
        Do not harass users, disclose private information
        unnecessarily, or reuse contact information for unsolicited
        marketing. The Terms govern User Content permissions,
        and the Privacy Policy explains our data handling.
      </Typography>

      <Typography variant="h5" gutterBottom>
        11. Insurance and Risk
      </Typography>

      <Typography paragraph>
        Confirm whether appropriate cargo coverage exists and
        review relevant limits and exclusions. Declaring a cargo
        value does not by itself purchase insurance or establish
        a particular liability limit.
      </Typography>

      <Typography paragraph>
        GoCourier Inc. does not itself provide insurance, and
        Platform use does not automatically insure a shipment.
        Responsibilities and remedies remain governed by
        applicable law and valid agreements.
      </Typography>

      <Typography variant="h5" gutterBottom>
        12. Account Restrictions and Deletion
      </Typography>

      <Typography paragraph>
        Accounts may be restricted for legitimate reasons under
        the Terms, including substantiated fraud, unsafe conduct,
        or material misuse. A good-faith complaint, refund request,
        safety report, or exercise of privacy rights is not
        itself misconduct.
      </Typography>

      <Typography paragraph>
        Account deletion is available through the process in the
        Terms and Privacy Policy. An unresolved transaction does
        not prevent you from requesting deletion, although lawful
        obligations and legally required records may remain.
      </Typography>

      <Typography variant="h5" gutterBottom>
        13. Contact
      </Typography>

      <Typography paragraph>
        Contact support@hagglefreight.com for assistance. Do not
        send passwords or full financial credentials through
        ordinary messages.
      </Typography>

      <Typography paragraph>
        The Terms of Service govern changes, document priority,
        applicable liability and indemnification provisions, and
        disputes. Nothing here removes a nonwaivable legal right.
      </Typography>
    </LegalPageLayout>
  );
};

export default CustomerAgreement;